import { useState, useRef, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { doc, updateDoc, arrayUnion, arrayRemove } from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { db, storage } from "@/lib/firebase";
import { useAuthContext } from "@/contexts/AuthContext";
import { useHouseholdContext } from "@/contexts/HouseholdContext";
import { useThemeContext } from "@/contexts/ThemeContext";
import { ThemeColors, FontFamilies, DIETARY_RESTRICTIONS, DASHBOARD_SECTIONS } from "@/lib/constants";
import type { ThemeColorKey, FontFamilyKey, DashboardSectionId } from "@/lib/constants";
import { Avatar } from "@/components/ui/Avatar";
import { Card } from "@/components/ui/Card";
import { HouseholdManagement } from "@/components/features/HouseholdManagement";
import { FeedbackModal } from "@/components/features/FeedbackModal";
import { AboutModal } from "@/components/features/AboutModal";
import { useHouseholdSubscription } from "@/hooks/useHouseholdSubscription";
import { usePaywall } from "@/contexts/PaywallContext";
import { useI18n } from "@/contexts/I18nContext";
import { LOCALE_NAMES } from "@/lib/i18n/locales";
import { LanguagePicker } from "@/components/i18n/LanguagePicker";
import { chooseBillingDestination, tierDisplayLabel } from "@/lib/subscription";
import { redirectToBillingPortal } from "@/lib/billing";
import {
  getCanonicalStores,
  getPreferredStoreId,
  type PreferredStoreId,
} from "@/lib/preferredStore";

const NAV_LINKS = [
  { labelKey: "nav.dashboard", href: "/dashboard", icon: "🏠" },
  { labelKey: "nav.calendar", href: "/events", icon: "📅" },
  { labelKey: "nav.chores", href: "/chores", icon: "✅" },
  { labelKey: "nav.shopping", href: "/shopping", icon: "🛒" },
  { labelKey: "nav.grocery", href: "/grocery", icon: "🥕" },
  { labelKey: "nav.meals", href: "/meals", icon: "🍲" },
  { labelKey: "nav.notifications", href: "/notifications", icon: "🔔" },
];

export function AccountPage() {
  const navigate = useNavigate();
  const { signOut } = useAuthContext();
  const { profile, household, householdId, loading: contextLoading } = useHouseholdContext();
  const {
    themeColor,
    fontFamily,
    setThemeColor,
    setFontFamily,
  } = useThemeContext();

  const { state: subscriptionState } = useHouseholdSubscription();
  const { openPaywall } = usePaywall();
  const { t, locale } = useI18n();

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [editingNickname, setEditingNickname] = useState(false);
  const [nickname, setNickname] = useState("");
  const [savingNickname, setSavingNickname] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [showAppearance, setShowAppearance] = useState(false);
  const [showHouseholds, setShowHouseholds] = useState(false);
  const [showDietary, setShowDietary] = useState(false);
  const [showDashboardLayout, setShowDashboardLayout] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showAbout, setShowAbout] = useState(false);
  const [showLanguage, setShowLanguage] = useState(false);
  const [showStore, setShowStore] = useState(false);
  const [savingStore, setSavingStore] = useState(false);
  const [showBudget, setShowBudget] = useState(false);
  const [savingBudget, setSavingBudget] = useState(false);
  const [openingBilling, setOpeningBilling] = useState(false);
  const [billingError, setBillingError] = useState<string | null>(null);

  // `key` matches iOS canonical (`AccountMenuView.swift:533, 551-552`):
  // lowercase `free / low / moderate / splurge`. Web previously wrote
  // a different set (tight/moderate/comfortable/generous) that iOS+
  // Android did not recognize. The i18n keys drive translated display.
  const BUDGET_OPTIONS = [
    { key: "free", labelKey: "account.budget.free.label", helpKey: "account.budget.free.help" },
    { key: "low", labelKey: "account.budget.low.label", helpKey: "account.budget.low.help" },
    { key: "moderate", labelKey: "account.budget.moderate.label", helpKey: "account.budget.moderate.help" },
    { key: "splurge", labelKey: "account.budget.splurge.label", helpKey: "account.budget.splurge.help" },
  ] as const;

  const isHouseholdOwner = !!profile?.uid && household?.household_owner === profile.uid;

  const handlePickBudget = async (key: string) => {
    if (!householdId || !isHouseholdOwner) return;
    setSavingBudget(true);
    try {
      await updateDoc(doc(db, "households", householdId), {
        budget_preference: key,
      });
    } catch (err) {
      console.error("Failed to save budget preference:", err);
    } finally {
      setSavingBudget(false);
    }
  };

  // Stores match iOS canonical (`RetailerStore.swift:11-15`). Values
  // written to Firestore are lowercase enum IDs (amazon/walmart/kroger/
  // instacart/target). Web previously wrote title-case strings + a
  // broader set (Costco, Trader Joe's, etc.) that iOS+Android did not
  // recognize — see `lib/preferredStore.ts` for back-compat read.
  const PREFERRED_STORES = getCanonicalStores();
  const currentPreferredStoreId = getPreferredStoreId(profile?.preferred_store);

  const handlePickStore = async (storeId: PreferredStoreId) => {
    if (!profile?.uid) return;
    setSavingStore(true);
    try {
      await updateDoc(doc(db, "member_profiles", profile.uid), {
        preferred_store: storeId,
      });
    } catch (err) {
      console.error("Failed to save preferred store:", err);
    } finally {
      setSavingStore(false);
    }
  };

  // Dashboard section order
  const [sectionOrder, setSectionOrder] = useState<DashboardSectionId[]>(
    DASHBOARD_SECTIONS.map((s) => s.id)
  );

  useEffect(() => {
    if (typeof window !== "undefined") {
      const saved = localStorage.getItem("dashboardSectionOrder");
      if (saved) {
        try {
          const parsed = JSON.parse(saved) as DashboardSectionId[];
          const validIds = new Set(DASHBOARD_SECTIONS.map((s) => s.id));
          if (parsed.every((id) => validIds.has(id)) && parsed.length === validIds.size) {
            setSectionOrder(parsed);
          }
        } catch {
          // ignore invalid JSON
        }
      }
    }
  }, []);

  function moveSection(index: number, direction: "up" | "down") {
    const newOrder = [...sectionOrder];
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    [newOrder[index], newOrder[swapIndex]] = [newOrder[swapIndex], newOrder[index]];
    setSectionOrder(newOrder);
    localStorage.setItem("dashboardSectionOrder", JSON.stringify(newOrder));
  }

  function resetSectionOrder() {
    const defaultOrder = DASHBOARD_SECTIONS.map((s) => s.id);
    setSectionOrder(defaultOrder);
    localStorage.removeItem("dashboardSectionOrder");
  }

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleManageBilling = async () => {
    setBillingError(null);
    // Routing decision is encapsulated in `chooseBillingDestination` so the
    // four-state matrix (paid Stripe / paid Apple / trial / no-sub) is
    // table-tested. See subscription.test.ts and BillingDestination type.
    const destination = chooseBillingDestination(subscriptionState);
    if (destination === "apple") {
      window.location.href = "https://apps.apple.com/account/subscriptions";
      return;
    }
    if (destination === "paywall") {
      openPaywall({
        title: t("account.billing.paywallTitle"),
        subtitle: t("account.billing.paywallSubtitle"),
      });
      return;
    }
    setOpeningBilling(true);
    try {
      await redirectToBillingPortal({ householdId: householdId ?? undefined });
    } catch (error) {
      console.error("Failed to open billing portal", error);
      setBillingError(t("account.billing.errorOpening"));
      setOpeningBilling(false);
    }
  };

  const handleTierBadgeClick = () => {
    if (subscriptionState.isActive) {
      void handleManageBilling();
    } else {
      openPaywall();
    }
  };

  const startEditNickname = () => {
    setNickname(profile?.nickname || profile?.name || "");
    setEditingNickname(true);
  };

  const saveNickname = async () => {
    if (!profile?.uid) return;
    setSavingNickname(true);
    try {
      const profileRef = doc(db, "member_profiles", profile.uid);
      await updateDoc(profileRef, { nickname: nickname.trim() });
      setEditingNickname(false);
    } catch (error) {
      console.error("Error saving nickname:", error);
    } finally {
      setSavingNickname(false);
    }
  };

  const handleAvatarUpload = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    if (!file || !profile?.uid) return;

    setUploadingAvatar(true);
    try {
      const { compressForAvatar } = await import("@/lib/imageCompressor");
      const compressed = await compressForAvatar(file);
      const storageRef = ref(storage, `member_profiles/${profile.uid}/avatar.jpg`);
      await uploadBytes(storageRef, compressed);
      const url = await getDownloadURL(storageRef);

      const profileRef = doc(db, "member_profiles", profile.uid);
      await updateDoc(profileRef, { profile_url: url });
    } catch (error) {
      console.error("Error uploading avatar:", error);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const toggleDietaryRestriction = async (restriction: string) => {
    if (!profile?.uid) return;
    const profileRef = doc(db, "member_profiles", profile.uid);
    const current = profile.dietary_restrictions ?? [];
    if (current.includes(restriction)) {
      await updateDoc(profileRef, { dietary_restrictions: arrayRemove(restriction) });
    } else {
      await updateDoc(profileRef, { dietary_restrictions: arrayUnion(restriction) });
    }
  };

  if (contextLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div
          className="w-8 h-8 border-3 border-t-transparent rounded-full animate-spin"
          style={{
            borderColor: "var(--accent-color, #00C7BE)",
            borderTopColor: "transparent",
          }}
        />
      </div>
    );
  }

  const themeColorKeys = Object.keys(ThemeColors) as ThemeColorKey[];
  const fontFamilyKeys = Object.keys(FontFamilies) as FontFamilyKey[];

  return (
    <div className="p-4 space-y-5">
      <h1 className="text-2xl font-bold">{t("account.title")}</h1>

      {/* Profile Header */}
      <Card>
        <div className="flex items-center gap-4">
          <div className="relative">
            <button
              onClick={() => fileInputRef.current?.click()}
              className="relative group"
              disabled={uploadingAvatar}
            >
              <Avatar
                src={profile?.profile_url}
                alt={profile?.nickname || profile?.name || ""}
                size={80}
              />
              <div className="absolute inset-0 rounded-full bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                <span className="text-white opacity-0 group-hover:opacity-100 text-xs font-medium">
                  Edit
                </span>
              </div>
              {uploadingAvatar && (
                <div className="absolute inset-0 rounded-full bg-black/40 flex items-center justify-center">
                  <div
                    className="w-5 h-5 border-2 border-t-transparent rounded-full animate-spin"
                    style={{ borderColor: "white", borderTopColor: "transparent" }}
                  />
                </div>
              )}
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleAvatarUpload}
              className="hidden"
            />
          </div>

          <div className="flex-1 min-w-0">
            {editingNickname ? (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={nickname}
                  onChange={(e) => setNickname(e.target.value)}
                  autoFocus
                  className="flex-1 px-2 py-1 rounded-md border border-gray-200 dark:border-gray-700 bg-transparent text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-[color:var(--accent-color)]"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveNickname();
                    if (e.key === "Escape") setEditingNickname(false);
                  }}
                />
                <button
                  onClick={saveNickname}
                  disabled={savingNickname}
                  className="text-sm font-medium shrink-0"
                  style={{ color: "var(--accent-color, #00C7BE)" }}
                >
                  {savingNickname ? t("account.nickname.saving") : t("account.nickname.save")}
                </button>
              </div>
            ) : (
              <button
                onClick={startEditNickname}
                className="text-left group"
              >
                <p className="font-bold text-lg leading-tight group-hover:underline">
                  {profile?.nickname || profile?.name || t("account.nickname.placeholder")}
                </p>
              </button>
            )}

            <p className="text-sm text-gray-500 mt-0.5">
              {household?.household_label || t("account.noHousehold")}
            </p>

            <div className="flex flex-wrap items-center gap-1.5 mt-1">
              {profile?.memberType && (
                <span
                  className="inline-block text-xs font-semibold px-2 py-0.5 rounded-full text-white capitalize"
                  style={{
                    backgroundColor: "var(--accent-color, #00C7BE)",
                  }}
                >
                  {profile.memberType}
                </span>
              )}
              <button
                onClick={handleTierBadgeClick}
                className={`inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full transition-colors ${
                  subscriptionState.isPastDue
                    ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 hover:bg-red-200 dark:hover:bg-red-900/50"
                    : subscriptionState.isActive
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 hover:bg-emerald-200 dark:hover:bg-emerald-900/50"
                      : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15"
                }`}
                aria-label={t("account.tierBadge.aria")}
              >
                {tierDisplayLabel(subscriptionState)}
              </button>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Navigation */}
      <Card padding="p-0">
        <div className="divide-y divide-gray-100 dark:divide-white/5">
          {NAV_LINKS.map((link) => (
            <Link
              key={link.href}
              to={link.href}
              className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              <span className="text-lg">{link.icon}</span>
              <span className="text-sm font-medium">{t(link.labelKey)}</span>
              <svg
                className="ml-auto text-gray-300"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Link>
          ))}
        </div>
      </Card>

      {/* Manage Billing */}
      <Card padding="p-0">
        <button
          onClick={handleManageBilling}
          disabled={openingBilling}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors disabled:opacity-60"
        >
          <span className="text-lg">{"\u{1F4B3}"}</span>
          <span className="text-sm font-medium">
            {openingBilling ? t("account.billing.opening") : t("account.billing.manage")}
          </span>
          <span
            className={`ml-auto text-xs font-semibold px-2 py-0.5 rounded-full ${
              subscriptionState.isPastDue
                ? "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300"
                : subscriptionState.isActive
                  ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300"
                  : "bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-400"
            }`}
          >
            {tierDisplayLabel(subscriptionState)}
          </span>
          <svg
            className="text-gray-300"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
        {billingError && (
          <p
            role="alert"
            className="px-4 pb-3 -mt-1 text-xs text-red-600 dark:text-red-400"
          >
            {billingError}
          </p>
        )}
      </Card>

      {/* Dietary Restrictions */}
      <div className="space-y-2">
        <button
          onClick={() => setShowDietary(!showDietary)}
          className="w-full flex items-center justify-between"
        >
          <p className="text-sm font-semibold text-gray-500">
            {t("account.dietary.title")}
          </p>
          <svg
            className={`text-gray-400 transition-transform ${
              showDietary ? "rotate-180" : ""
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {showDietary && (
          <Card>
            <div className="space-y-3">
              <div className="flex flex-wrap gap-2">
                {DIETARY_RESTRICTIONS.map((restriction) => {
                  const isSelected = (
                    profile?.dietary_restrictions ?? []
                  ).includes(restriction);
                  return (
                    <button
                      key={restriction}
                      onClick={() => toggleDietaryRestriction(restriction)}
                      className={`rounded-full px-3 py-1 text-sm font-medium transition-colors ${
                        isSelected
                          ? "bg-[color:var(--accent-color)] text-white"
                          : "bg-gray-100 text-gray-700 dark:bg-white/10 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15"
                      }`}
                    >
                      {restriction}
                    </button>
                  );
                })}
              </div>
              {profile?.allergies && profile.allergies.length > 0 && (
                <div>
                  <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">
                    {t("account.allergies")}
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {profile.allergies.map((allergy) => (
                      <span
                        key={allergy}
                        className="text-xs px-2 py-0.5 rounded-full bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      >
                        {allergy}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}
      </div>

      {/* Customize Appearance */}
      <div className="space-y-2">
        <button
          onClick={() => setShowAppearance(!showAppearance)}
          className="w-full flex items-center justify-between"
        >
          <p className="text-sm font-semibold text-gray-500">
            {t("account.appearance.title")}
          </p>
          <svg
            className={`text-gray-400 transition-transform ${
              showAppearance ? "rotate-180" : ""
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {showAppearance && (
          <div className="space-y-4">
            {/* Color Picker */}
            <Card>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                {t("account.appearance.themeColor")}
              </p>
              <div className="grid grid-cols-5 gap-3 justify-items-center">
                {themeColorKeys.map((key) => {
                  const color = ThemeColors[key];
                  const isSelected = key === themeColor;
                  return (
                    <button
                      key={key}
                      onClick={() => setThemeColor(key)}
                      className="relative w-10 h-10 rounded-full flex items-center justify-center transition-transform hover:scale-110"
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    >
                      {isSelected && (
                        <svg
                          width="18"
                          height="18"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="white"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>

            {/* Font Family */}
            <Card padding="p-0">
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide px-3 pt-3 pb-2">
                {t("account.appearance.fontFamily")}
              </p>
              <div className="divide-y divide-gray-100 dark:divide-white/5">
                {fontFamilyKeys.map((key) => {
                  const font = FontFamilies[key];
                  const isSelected = key === fontFamily;
                  return (
                    <button
                      key={key}
                      onClick={() => setFontFamily(key)}
                      className="w-full flex items-center justify-between px-3 py-2.5 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
                    >
                      <span
                        className={`text-sm ${isSelected ? "font-semibold" : ""}`}
                        style={{
                          fontFamily: font.value,
                          color: isSelected
                            ? "var(--accent-color, #00C7BE)"
                            : undefined,
                        }}
                      >
                        {font.name}
                      </span>
                      {isSelected && (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          style={{ color: "var(--accent-color, #00C7BE)" }}
                        >
                          <path d="M20 6L9 17l-5-5" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>
          </div>
        )}
      </div>

      {/* Households */}
      <div className="space-y-2">
        <button
          onClick={() => setShowHouseholds(!showHouseholds)}
          className="w-full flex items-center justify-between"
        >
          <p className="text-sm font-semibold text-gray-500">{t("account.households.title")}</p>
          <svg
            className={`text-gray-400 transition-transform ${
              showHouseholds ? "rotate-180" : ""
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {showHouseholds && (
          <div className="space-y-3">
            <HouseholdManagement />
            <Link
              to="/account/households"
              className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[color:var(--accent-color)] hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
            >
              <span>{t("households.manageLink")}</span>
              <svg
                className="ml-auto"
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M9 18l6-6-6-6" />
              </svg>
            </Link>
          </div>
        )}
      </div>

      {/* Dashboard Layout */}
      <div className="space-y-2">
        <button
          onClick={() => setShowDashboardLayout(!showDashboardLayout)}
          className="w-full flex items-center justify-between"
        >
          <p className="text-sm font-semibold text-gray-500">
            {t("account.dashboardLayout.title")}
          </p>
          <svg
            className={`text-gray-400 transition-transform ${
              showDashboardLayout ? "rotate-180" : ""
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {showDashboardLayout && (
          <Card padding="p-0">
            <div className="divide-y divide-gray-100 dark:divide-white/5">
              {sectionOrder.map((sectionId, index) => {
                const section = DASHBOARD_SECTIONS.find((s) => s.id === sectionId);
                if (!section) return null;
                const isFirst = index === 0;
                const isLast = index === sectionOrder.length - 1;
                return (
                  <div
                    key={sectionId}
                    className="flex items-center gap-3 px-4 py-3"
                  >
                    <span className="text-lg">{section.icon}</span>
                    <span className="flex-1 text-sm font-medium">
                      {section.label}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => moveSection(index, "up")}
                        disabled={isFirst}
                        className={`p-1 rounded transition-colors ${
                          isFirst
                            ? "text-gray-200 dark:text-gray-700 cursor-not-allowed"
                            : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10"
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M18 15l-6-6-6 6" />
                        </svg>
                      </button>
                      <button
                        onClick={() => moveSection(index, "down")}
                        disabled={isLast}
                        className={`p-1 rounded transition-colors ${
                          isLast
                            ? "text-gray-200 dark:text-gray-700 cursor-not-allowed"
                            : "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10"
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M6 9l6 6 6-6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-4 py-3 border-t border-gray-100 dark:border-white/5">
              <button
                onClick={resetSectionOrder}
                className="text-sm font-medium"
                style={{ color: "var(--accent-color, #00C7BE)" }}
              >
                {t("account.dashboardLayout.reset")}
              </button>
            </div>
          </Card>
        )}
      </div>

      {/* Language */}
      <div className="space-y-2">
        <button
          onClick={() => setShowLanguage(!showLanguage)}
          className="w-full flex items-center justify-between"
        >
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-500">
              {t("account.language.title")}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {t("account.language.subtitle", { language: LOCALE_NAMES[locale].native })}
            </p>
          </div>
          <svg
            className={`text-gray-400 transition-transform ${
              showLanguage ? "rotate-180" : ""
            }`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>

        {showLanguage && (
          <Card padding="p-0">
            <LanguagePicker />
          </Card>
        )}
      </div>

      {/* Preferred store */}
      <div className="space-y-2">
        <button
          onClick={() => setShowStore(!showStore)}
          className="w-full flex items-center justify-between"
        >
          <div className="text-left">
            <p className="text-sm font-semibold text-gray-500">
              {t("account.preferredStore.title")}
            </p>
            <p className="text-xs text-gray-400 mt-0.5">
              {(() => {
                const opt = PREFERRED_STORES.find((s) => s.id === currentPreferredStoreId);
                return opt?.label ?? t("account.notSet");
              })()}
            </p>
          </div>
          <svg
            className={`text-gray-400 transition-transform ${showStore ? "rotate-180" : ""}`}
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M6 9l6 6 6-6" />
          </svg>
        </button>
        {showStore && (
          <Card>
            <div className="flex flex-wrap gap-2">
              {PREFERRED_STORES.map((store) => {
                const active = currentPreferredStoreId === store.id;
                return (
                  <button
                    key={store.id}
                    type="button"
                    onClick={() => handlePickStore(store.id)}
                    disabled={savingStore}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                      active
                        ? "bg-[color:var(--accent-color)] text-white"
                        : "bg-gray-100 dark:bg-white/10 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-white/15"
                    }`}
                  >
                    {store.label}
                  </button>
                );
              })}
            </div>
          </Card>
        )}
      </div>

      {/* Household budget preference (owner-only) */}
      {isHouseholdOwner && (
        <div className="space-y-2">
          <button
            onClick={() => setShowBudget(!showBudget)}
            className="w-full flex items-center justify-between"
          >
            <div className="text-left">
              <p className="text-sm font-semibold text-gray-500">
                {t("account.budget.title")}
              </p>
              <p className="text-xs text-gray-400 mt-0.5">
                {(() => {
                  const opt = BUDGET_OPTIONS.find((o) => o.key === household?.budget_preference);
                  return opt ? t(opt.labelKey) : t("account.notSet");
                })()}
              </p>
            </div>
            <svg
              className={`text-gray-400 transition-transform ${showBudget ? "rotate-180" : ""}`}
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
          {showBudget && (
            <Card>
              <p className="text-xs text-gray-500 mb-3">
                {t("account.budget.help")}
              </p>
              <div className="space-y-1.5">
                {BUDGET_OPTIONS.map((opt) => {
                  const active = household?.budget_preference === opt.key;
                  return (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => handlePickBudget(opt.key)}
                      disabled={savingBudget}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm transition-colors ${
                        active
                          ? "bg-[color:var(--accent-color)] text-white"
                          : "bg-gray-50 dark:bg-white/5 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10"
                      }`}
                    >
                      <div className="text-left">
                        <p className="font-medium">{t(opt.labelKey)}</p>
                        <p className={`text-xs ${active ? "text-white/80" : "text-gray-400"}`}>
                          {t(opt.helpKey)}
                        </p>
                      </div>
                      {active && (
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12" />
                        </svg>
                      )}
                    </button>
                  );
                })}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Report Feedback */}
      <button
        onClick={() => setShowFeedback(true)}
        className="w-full flex items-center gap-3 px-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
      >
        <span className="text-lg">{"\u{1F4E3}"}</span>
        <span className="text-sm font-medium">{t("account.feedback.button")}</span>
        <svg
          className="ml-auto text-gray-300"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M9 18l6-6-6-6" />
        </svg>
      </button>

      {/* About */}
      <Card padding="p-0">
        <button
          onClick={() => setShowAbout(true)}
          className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 dark:hover:bg-white/5 transition-colors"
        >
          <span className="text-lg">{"ℹ️"}</span>
          <span className="text-sm font-medium">{t("account.about.row")}</span>
          <svg
            className="ml-auto text-gray-300"
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M9 18l6-6-6-6" />
          </svg>
        </button>
      </Card>

      {/* Sign Out */}
      <button
        onClick={handleSignOut}
        className="w-full py-3 rounded-xl text-red-500 font-semibold text-sm border border-red-200 dark:border-red-900/30 hover:bg-red-50 dark:hover:bg-red-900/10 transition-colors"
      >
        {t("account.signOut")}
      </button>

      {/* Feedback Modal */}
      <FeedbackModal
        isOpen={showFeedback}
        onClose={() => setShowFeedback(false)}
        householdId={householdId ?? ""}
        userId={profile?.uid ?? ""}
      />

      {/* About Modal */}
      <AboutModal isOpen={showAbout} onClose={() => setShowAbout(false)} />
    </div>
  );
}
